/**
 * The Map view of a list page (piece 13; decision 0343): the Web Mercator
 * arithmetic that fits a set of pins, places a raster tile and offsets a pin
 * over the picture, as pure functions over facts the screen supplies. 🔨 No
 * map library: the screen draws tiles or one static image through the API's
 * own routes and lays its own pins over them, and the provider is the one
 * Settings › Integrations › Maps names (decision 0319).
 */
export const MAP_TILE_SIZE = 256;
export const MAP_MIN_ZOOM = 2;
export const MAP_MAX_ZOOM = 18;
/** One pin alone is shown at street scale rather than at the world's. */
export const MAP_SINGLE_PIN_ZOOM = 14;
/** 💰 Google Static Maps answers at most 640 pixels a side on the standard plan; a wider map is cut to it. */
export const STATIC_MAP_MAX_PX = 640;
/** The canvas height the screen draws at when it cannot measure itself; the stylesheet carries the same figure in rem. */
export const MAP_VIEW_HEIGHT_PX = 480;
/** The padding kept around the fitted pins, so a pin never stands on the edge. */
export const MAP_FIT_PADDING_PX = 40;
/** Web Mercator's own limit; a latitude past it has no place on the picture. */
const MAX_LATITUDE = 85.05112878;

/** The list pages that draw a map: a Location has coordinates of its own, and a Site pins its building. */
export const MAPPABLE_RECORDS = ['LocationRecord', 'SiteRecord'] as const;
export type MappableRecord = (typeof MAPPABLE_RECORDS)[number];

export function isMappable(recordType: string): recordType is MappableRecord {
  return (MAPPABLE_RECORDS as readonly string[]).includes(recordType);
}

export interface MapPin {
  id: string;
  label: string;
  latitude: number;
  longitude: number;
}

export interface MapCenter {
  latitude: number;
  longitude: number;
  zoom: number;
}

export interface MapSize {
  width: number;
  height: number;
}

export interface MapPoint {
  x: number;
  y: number;
}

/** A tile to draw and where its top-left corner falls on the canvas. */
export interface MapTile {
  z: number;
  x: number;
  y: number;
  left: number;
  top: number;
}

export interface PinOffset {
  pin: MapPin;
  left: number;
  top: number;
}

const clampLatitude = (latitude: number): number => Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, latitude));
const clampZoom = (zoom: number): number => Math.max(MAP_MIN_ZOOM, Math.min(MAP_MAX_ZOOM, Math.round(zoom)));
const worldSize = (zoom: number): number => MAP_TILE_SIZE * 2 ** zoom;

/** A place as a pixel on the world at a zoom: the projection every tile server draws by. */
export function worldPoint(latitude: number, longitude: number, zoom: number): MapPoint {
  const size = worldSize(zoom);
  const lat = (clampLatitude(latitude) * Math.PI) / 180;
  const x = ((longitude + 180) / 360) * size;
  const y = ((1 - Math.log(Math.tan(lat) + 1 / Math.cos(lat)) / Math.PI) / 2) * size;
  return { x, y };
}

/** The projection back: a pixel on the world at a zoom as a place. */
export function worldToPlace(point: MapPoint, zoom: number): { latitude: number; longitude: number } {
  const size = worldSize(zoom);
  const longitude = (point.x / size) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * point.y) / size;
  const latitude = (180 / Math.PI) * Math.atan(Math.sinh(n));
  return { latitude: clampLatitude(latitude), longitude };
}

/**
 * The center and zoom that show every pin with the padding kept, or the one
 * pin at street scale; null when there is nothing to fit.
 */
export function fitPins(pins: readonly MapPin[], size: MapSize, padding = MAP_FIT_PADDING_PX): MapCenter | null {
  if (pins.length === 0) return null;
  if (pins.length === 1) return { latitude: clampLatitude(pins[0]!.latitude), longitude: pins[0]!.longitude, zoom: MAP_SINGLE_PIN_ZOOM };
  const points = pins.map((pin) => worldPoint(pin.latitude, pin.longitude, 0));
  const minX = Math.min(...points.map((p) => p.x));
  const maxX = Math.max(...points.map((p) => p.x));
  const minY = Math.min(...points.map((p) => p.y));
  const maxY = Math.max(...points.map((p) => p.y));
  const room = { width: Math.max(1, size.width - 2 * padding), height: Math.max(1, size.height - 2 * padding) };
  let zoom = MAP_MIN_ZOOM;
  for (let z = MAP_MAX_ZOOM; z >= MAP_MIN_ZOOM; z -= 1) {
    if ((maxX - minX) * 2 ** z <= room.width && (maxY - minY) * 2 ** z <= room.height) {
      zoom = z;
      break;
    }
  }
  const place = worldToPlace({ x: (minX + maxX) / 2, y: (minY + maxY) / 2 }, 0);
  return { ...place, zoom };
}

/** Where each pin stands on a canvas of this size centered here; a pin off the canvas is answered too, and the screen clips it. */
export function pinOffsets(pins: readonly MapPin[], center: MapCenter, size: MapSize): PinOffset[] {
  const c = worldPoint(center.latitude, center.longitude, center.zoom);
  return pins.map((pin) => {
    const p = worldPoint(pin.latitude, pin.longitude, center.zoom);
    return { pin, left: size.width / 2 + (p.x - c.x), top: size.height / 2 + (p.y - c.y) };
  });
}

/** The tiles that cover a canvas of this size centered here, each with its corner; x wraps around the date line and y never leaves the world. */
export function tileGrid(center: MapCenter, size: MapSize): MapTile[] {
  const z = clampZoom(center.zoom);
  const n = 2 ** z;
  const c = worldPoint(center.latitude, center.longitude, z);
  const left0 = c.x - size.width / 2;
  const top0 = c.y - size.height / 2;
  const tiles: MapTile[] = [];
  for (let ty = Math.floor(top0 / MAP_TILE_SIZE); ty * MAP_TILE_SIZE < top0 + size.height; ty += 1) {
    if (ty < 0 || ty >= n) continue;
    for (let tx = Math.floor(left0 / MAP_TILE_SIZE); tx * MAP_TILE_SIZE < left0 + size.width; tx += 1) {
      tiles.push({ z, x: ((tx % n) + n) % n, y: ty, left: tx * MAP_TILE_SIZE - left0, top: ty * MAP_TILE_SIZE - top0 });
    }
  }
  return tiles;
}

/** The center after the picture was dragged by these pixels: dragging right shows what was to the left. */
export function panCenter(center: MapCenter, dx: number, dy: number): MapCenter {
  const c = worldPoint(center.latitude, center.longitude, center.zoom);
  const size = worldSize(center.zoom);
  const place = worldToPlace({ x: Math.max(0, Math.min(size, c.x - dx)), y: Math.max(0, Math.min(size, c.y - dy)) }, center.zoom);
  return { ...place, zoom: center.zoom };
}

/** The center one step in or out, held within the zoom range. */
export function zoomed(center: MapCenter, step: 1 | -1): MapCenter {
  return { ...center, zoom: clampZoom(center.zoom + step) };
}

/** The canvas Google Static Maps can answer for: the same size, cut to its cap on each side. */
export function staticMapSize(size: MapSize): MapSize {
  return { width: Math.max(1, Math.min(STATIC_MAP_MAX_PX, Math.round(size.width))), height: Math.max(1, Math.min(STATIC_MAP_MAX_PX, Math.round(size.height))) };
}

/** The query the API's `maps/static` route takes: the center, the zoom and the size, with nothing about the key. */
export function staticMapQuery(center: MapCenter, size: MapSize): string {
  const cut = staticMapSize(size);
  const query = new URLSearchParams();
  query.set('center', `${center.latitude.toFixed(6)},${center.longitude.toFixed(6)}`);
  query.set('zoom', String(clampZoom(center.zoom)));
  query.set('size', `${cut.width}x${cut.height}`);
  return query.toString();
}

/** The facts a `maps/static` request carries, read back from its query; null when any of them is malformed. */
export function parseStaticMapQuery(query: { center?: string; zoom?: string; size?: string }): { center: MapCenter; size: MapSize } | null {
  const parts = (query.center ?? '').split(',');
  const latitude = Number(parts[0]);
  const longitude = Number(parts[1]);
  const zoom = Number(query.zoom);
  const dims = (query.size ?? '').split('x').map((d) => Number(d));
  if (parts.length !== 2 || !Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  if (!Number.isInteger(zoom) || zoom < MAP_MIN_ZOOM || zoom > MAP_MAX_ZOOM) return null;
  if (dims.length !== 2 || dims.some((d) => !Number.isInteger(d) || d < 1 || d > STATIC_MAP_MAX_PX)) return null;
  return { center: { latitude, longitude, zoom }, size: { width: dims[0]!, height: dims[1]! } };
}

/** Whether a tile address is one the world has at that zoom. */
export function isTileAddress(z: number, x: number, y: number): boolean {
  if (!Number.isInteger(z) || !Number.isInteger(x) || !Number.isInteger(y)) return false;
  if (z < MAP_MIN_ZOOM || z > MAP_MAX_ZOOM) return false;
  const n = 2 ** z;
  return x >= 0 && x < n && y >= 0 && y < n;
}
